import React from 'react';
import './UserBadgesPopup.css';
import Api from '../../../../utils/Api';

import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Avatar,
} from '@mui/material';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { Alert, Snackbar } from '@mui/material';
import { Backdrop, CircularProgress } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';

// Page modifier par Frédérick BETHERMAT - 17-05-2026

/**
 * Permet d'assigner ou de retirer des badges selon le rôle de l'utilisateur connecté.
 */
class UserBadgesPopup extends React.Component {
  constructor(props) {
    super(props);

    // Compteur de requêtes en cours, utilisé pour piloter l'indicateur de chargement
    this.loadingCount = 0;

    this.state = {
      loading: false, // Indique si un chargement est en cours
      leftBadges: [], // Liste des badges disponibles à assigner
      ownBadges: [], // Liste des badges déjà obtenus par l'utilisateur
      showSuccessMessage: false, // Contrôle l'affichage du message de succès
      badgeIdToAssignErrorMessage: '', // Message d'erreur pour la sélection du badge
      badgeIdToAssign: 0, // Identifiant du badge sélectionné pour l'assignation
      confirmRemoveOpen: false, // Contrôle l'ouverture du dialogue de confirmation
      badgeIdToRemove: null, // Badge ciblé par le retrait
      showRemoveSuccessMessage: false, // Contrôle l'affichage du message de succès du retrait

      // Définition des colonnes de la grille des badges obtenus
      columns: [
        {
          field: 'imagePath',
          headerName: 'Image',
          width: 70,
          // Affichage de l'avatar du badge avec une ombre colorée selon sa catégorie
          renderCell: (params) => {
            return (
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Avatar
                  alt={params.row.title}
                  src={params.row.imagePath}
                  sx={{ boxShadow: `0 0 4px 6px ${params.row.category_color}` }}
                />
                ;
              </div>
            );
          },
        },
        { field: 'title', headerName: 'Titre', flex: 1 },
        {
          field: 'removeBadgeAction',
          minWidth: 200,
          headerName: '',
          sortable: false,
          // Bouton ouvrant le dialogue de confirmation avant de retirer un badge
          renderCell: (params) => {
            const onClick = (e) => {
              e.stopPropagation(); // Empêche la propagation du clic à la ligne du tableau
              this.setState({
                confirmRemoveOpen: true,
                badgeIdToRemove: params.row.id,
              });
            };

            return (
              <Button variant="outlined" onClick={onClick}>
                retirer le badge
              </Button>
            );
          },
        },
      ],
    };

    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleCloseSuccessMessage = this.handleCloseSuccessMessage.bind(this);
    this.handleCloseConfirmRemove = this.handleCloseConfirmRemove.bind(this);
    this.handleConfirmRemove = this.handleConfirmRemove.bind(this);
    this.clearForm = this.clearForm.bind(this);
  }

  // Ferme le dialogue de confirmation sans effectuer de retrait
  handleCloseConfirmRemove() {
    this.setState({ confirmRemoveOpen: false, badgeIdToRemove: null });
  }

  // Confirme le retrait du badge sélectionné
  handleConfirmRemove() {
    this.removeBadge(this.state.badgeIdToRemove);
    this.setState({ confirmRemoveOpen: false, badgeIdToRemove: null });
  }

  // Fonction qui change la valeur du champ quand on tape dedans
  handleChange(event) {
    const { name, value } = event.target;
    this.setState({
      [name]: value,
    });
  }

  // Fonction qui envoie les données du formulaire pour l'assignation d'un badge
  handleSubmit(event) {
    event.preventDefault();

    // Vérification qu'un badge a bien été sélectionné avant l'envoi
    if (this.state.badgeIdToAssign === 0) {
      this.setState({
        badgeIdToAssignErrorMessage: 'Veuillez sélectionner un badge',
      });
      return;
    }

    this.addStep();

    // Appel API pour assigner le badge sélectionné à l'utilisateur
    Api.post('/user/assign-badge', {
      user_id: this.props.selectedUser.id,
      badge_id: this.state.badgeIdToAssign,
    })
      .then(() => {
        // Mise à jour de l'affichage et notification de succès
        this.refreshBadgesAssignation();
        this.setState({ showSuccessMessage: true });
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.removeStep();
      });
  }

  // Fonction qui permet de vider le formulaire
  clearForm() {
    this.setState({ badgeIdToAssign: null, badgeIdToAssignErrorMessage: '' });
  }

  // Fonction qui permet de rafraîchir les badges assignés
  async refreshBadgesAssignation(delay = false) {
    this.addStep();
    // Délai optionnel de 500ms pour laisser le temps à l'API de traiter les changements
    setTimeout(
      () => {
        this.refreshOwnBadges();
        this.refreshPendingBadges();
        this.removeStep();
        this.clearForm();
      },
      delay ? 500 : 0,
    );
  }

  /**
   * Rafraîchir la liste des badges pouvant encore être assignés à l'utilisateur
   */
  refreshPendingBadges() {
    this.addStep();
    Api.get('/user/' + this.props.selectedUser.id + '/badges-left')
      .then((response) => {
        this.setState({ leftBadges: response.data.badges });
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.removeStep();
      });
  }

  /**
   * Enlever un badge à un utilisateur
   */
  removeBadge(badgeId) {
    this.addStep();
    Api.post('/user/remove-badge', {
      user_id: this.props.selectedUser.id,
      badge_id: badgeId,
    })
      .then(() => {
        // Actualisation de la liste et notification de succès après le retrait
        this.refreshBadgesAssignation();
        this.setState({ showRemoveSuccessMessage: true });
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.removeStep();
      });
  }

  /**
   * Rafraîchir la liste des badges de l'utilisateur
   */
  refreshOwnBadges() {
    this.addStep();
    Api.get('/user/' + this.props.selectedUser.id + '/badges')
      .then((response) => {
        this.setState({ ownBadges: response.data.badges });
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.removeStep();
      });
  }

  // Fonction qui permet de fermer le message de succès
  handleCloseSuccessMessage() {
    this.setState({ showSuccessMessage: false });
  }

  /**
   * Ajout d'une étape à la barre de chargement
   */
  addStep() {
    this.loadingCount++;

    // Déclenche l'affichage du spinner si ce n'est pas déjà le cas
    if (this.loadingCount > 0 && !this.state.loading) {
      this.setState({ loading: true });
    }
  }

  /**
   * Enlever une étape à la barre de chargement
   */
  removeStep() {
    this.loadingCount--;

    // Empêche le compteur de passer en négatif
    if (this.loadingCount < 0) {
      this.loadingCount = 0;
    }

    // Masque le spinner uniquement quand toutes les requêtes sont terminées
    if (this.loadingCount <= 0 && this.state.loading) {
      this.setState({ loading: false });
    }
  }

  render() {
    const { onlyRemove } = this.props;

    return (
      <>
        <Dialog
          open={this.props.isOpen ?? false}
          onClose={this.props.handleClose}
        >
          {/* Titre dynamique selon le mode (assignation ou retrait uniquement) */}
          <DialogTitle>
            {onlyRemove ? 'Retrait des badges' : 'Assignation des badges'}
          </DialogTitle>
          <DialogContent className={'badge-popup'}>
            <DialogContentText>
              {/* Overlay de chargement affiché pendant les appels API */}
              <Backdrop
                sx={{
                  color: '#fff',
                  zIndex: (theme) => theme.zIndex.drawer + 1,
                }}
                open={this.state.loading}
              >
                <CircularProgress color="inherit" />
              </Backdrop>
              {/* Notification de succès affichée après une assignation réussie */}
              <Snackbar
                onClose={this.handleCloseSuccessMessage}
                open={this.state.showSuccessMessage}
                autoHideDuration={3000}
              >
                <Alert
                  onClose={this.handleCloseSuccessMessage}
                  severity="success"
                  sx={{ width: '100%' }}
                  md={{ minWidth: '300px' }}
                >
                  Le badge a été assigné avec succès !
                </Alert>
              </Snackbar>
              {/* Notification de succès affichée après un retrait réussi */}
              <Snackbar
                onClose={() =>
                  this.setState({ showRemoveSuccessMessage: false })
                }
                open={this.state.showRemoveSuccessMessage}
                autoHideDuration={3000}
              >
                <Alert
                  onClose={() =>
                    this.setState({ showRemoveSuccessMessage: false })
                  }
                  severity="success"
                  sx={{ width: '100%' }}
                  md={{ minWidth: '300px' }}
                >
                  Le badge a été retiré avec succès !
                </Alert>
              </Snackbar>
              {/* Formulaire d'assignation masqué en mode "retrait uniquement" */}
              {!onlyRemove && (
                <form>
                  {/* Sélecteur de badge à assigner */}
                  <FormControl fullWidth>
                    <InputLabel id="badge-select-label">
                      Badge à assigner
                    </InputLabel>
                    <Select
                      id="badge-select"
                      required
                      label="Badge à assigner"
                      name="badgeIdToAssign"
                      labelId="badge-select-label"
                      className="badge-selector"
                      value={this.state.badgeIdToAssign}
                      error={this.state.badgeIdToAssignErrorMessage !== ''}
                      helperText={
                        this.state.badgeIdToAssignErrorMessage === ''
                          ? ''
                          : this.state.badgeIdToAssignErrorMessage
                      }
                      onChange={this.handleChange}
                    >
                      {/* Message si aucun badge n'est disponible, sinon liste des badges */}
                      {this.state.leftBadges.length === 0 ? (
                        <MenuItem disabled value={0}>
                          Aucun badge disponible
                        </MenuItem>
                      ) : (
                        this.state.leftBadges.map((badge) => {
                          return (
                            <MenuItem value={badge.id} key={badge.id}>
                              <div className="badge-row">
                                <Avatar
                                  className="badge"
                                  alt={badge.title}
                                  src={badge.imagePath}
                                  sx={{
                                    boxShadow: `0 0 4px 8px ${badge.category_color}`,
                                  }}
                                />
                                {badge.title}
                              </div>
                            </MenuItem>
                          );
                        })
                      )}
                    </Select>
                  </FormControl>

                  {/* Bouton de soumission du formulaire d'assignation */}
                  <FormControl fullWidth>
                    <Button
                      variant="contained"
                      className={'mt-2'}
                      onClick={this.handleSubmit}
                    >
                      Assigner le badge
                    </Button>
                  </FormControl>
                </form>
              )}
              {/* Séparateur entre le formulaire et la liste des badges obtenus */}
              {!onlyRemove && <hr />}
              Liste des badges obtenus
              {/* Tableau affichant les badges actuellement détenus par l'utilisateur */}
              <DataGrid
                autoHeight={true}
                rows={this.state.ownBadges ?? []}
                columns={this.state.columns}
                pageSize={5}
                rowsPerPageOptions={[5]}
              />
            </DialogContentText>
          </DialogContent>
        </Dialog>

        {/* Dialogue de confirmation pour le retrait d'un badge */}
        <Dialog
          open={this.state.confirmRemoveOpen}
          onClose={this.handleCloseConfirmRemove}
        >
          <DialogTitle>Confirmer le retrait</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Êtes-vous sûr de vouloir retirer ce badge à l'utilisateur ?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              color="error"
              onClick={this.handleConfirmRemove}
            >
              Confirmer
            </Button>
            <Button variant="outlined" onClick={this.handleCloseConfirmRemove}>
              Annuler
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
}

export default UserBadgesPopup;
